import { ClipboardCheck, RefreshCw, RotateCcw, Send, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { api, type VnnicSyncOutcomeRequest, type VnnicSyncRow, type VnnicSyncStatus, type VnnicSyncTransaction } from '../../../api';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../../components/blocks';
import { EntityRef } from '../../../components/EntityRef';
import { KeyValue } from '../../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, ErrorState, Loading } from '../../../components/States';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { DOSSIER } from '../../domains/status';
import { ActionDialog, EnumBadge, FormActions, FormGrid, MfaHint, SelectField, SuccessNote, useEnumLabel, useEnumOptions, When } from '../ui';
import { EvidenceHelp, Payload, ReceiptsTable, SYNC_STATUS, SYNC_STATUS_ORDER, SyncErrorCode, TX_STATUS, useRetained, useWrite, WriteError } from './shared';

const LIST_LIMIT = 200;

/** A recorded outcome answers a transaction that was sent but not yet answered. */
const canRecordOutcome = (tx: VnnicSyncTransaction) => tx.status === 'SUBMITTED' || tx.status === 'UNKNOWN';
/** Only a sync VNNIC turned down can be resubmitted; everything else is driven by the sync worker. */
const canResubmit = (row: { status: VnnicSyncStatus }) => row.status === 'REJECTED' || row.status === 'CORRECTION_REQUIRED';

function isSyncStatus(value: string | null): value is VnnicSyncStatus {
  return value !== null && value in SYNC_STATUS;
}

// ---------------------------------------------------------------------------
// Actions

type OutcomeKind = VnnicSyncOutcomeRequest['outcome'];
const OUTCOMES: OutcomeKind[] = ['ACKNOWLEDGED', 'REJECTED', 'CORRECTION_REQUIRED'];

function RecordOutcomeForm({ tx, onDone, onCancel }: { tx: VnnicSyncTransaction; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  const [outcome, setOutcome] = useState<OutcomeKind>('ACKNOWLEDGED');
  const [reference, setReference] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [evidence, setEvidence] = useState('');
  const [note, setNote] = useState('');
  const write = useWrite((req: VnnicSyncOutcomeRequest, opts) => api.recordVnnicSyncOutcome(tx.id, req, opts));

  const acknowledged = outcome === 'ACKNOWLEDGED';
  const complete = evidence.trim() !== '' && (acknowledged ? reference.trim() !== '' : code.trim() !== '' && message.trim() !== '');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!complete) return;
    const base = {
      evidence_object_key: evidence.trim(),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    const req: VnnicSyncOutcomeRequest = acknowledged
      ? { ...base, outcome: 'ACKNOWLEDGED', reference: reference.trim() }
      : { ...base, outcome, code: code.trim(), message: message.trim() };
    if (await write.run(req)) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      {tx.status === 'UNKNOWN' ? <Callout tone="serious">{t('vnnic.sync.outcome.unknown')}</Callout> : null}
      <FormGrid>
        <SelectField label={t('vnnic.sync.outcome.outcome')} value={outcome} options={options('txStatus', OUTCOMES)} onChange={(v) => v && setOutcome(v)} disabled={write.busy} />
        {acknowledged ? (
          <Field label={t('vnnic.sync.outcome.reference')}>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} disabled={write.busy} autoComplete="off" required />
          </Field>
        ) : (
          <>
            <Field label={t('vnnic.sync.outcome.code')}>
              <Input value={code} onChange={(e) => setCode(e.target.value)} disabled={write.busy} autoComplete="off" required />
            </Field>
            <Field label={t('vnnic.sync.outcome.message')} className="sm:col-span-2">
              <Input value={message} onChange={(e) => setMessage(e.target.value)} disabled={write.busy} autoComplete="off" required />
            </Field>
          </>
        )}
        <Field label={t('vnnic.sync.outcome.evidence')} className="sm:col-span-2" hint={<EvidenceHelp />}>
          <Input
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            disabled={write.busy}
            autoComplete="off"
            placeholder="evidence/vnnic/…"
            required
            className="font-mono"
          />
        </Field>
        <Field label={t('vnnic.sync.outcome.note')} className="sm:col-span-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={write.busy} rows={2} maxLength={1000} />
        </Field>
      </FormGrid>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !complete}>
          {write.busy ? t('vnnic.sync.outcome.busy') : t('vnnic.sync.outcome.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

/** Resubmits a rejected dossier sync; shared with the Corrections page. */
export function ResubmitForm({ dossierId, version, onDone, onCancel }: { dossierId: string; version?: number; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [reason, setReason] = useState('');
  const write = useWrite((r: string, opts) => api.resubmitVnnicSync(dossierId, r, opts));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!reason.trim()) return;
    if (await write.run(reason.trim())) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <p className="text-sm text-muted-foreground">{version === undefined ? t('vnnic.sync.resubmit.body') : t('vnnic.sync.resubmit.bodyVersion', { version })}</p>
      <Field label={t('common.auditedReason')}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} rows={3} maxLength={1000} required />
      </Field>
      <MfaHint permission="regulatory_report.manage" />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !reason.trim()}>
          <Send /> {write.busy ? t('vnnic.sync.resubmit.busy') : t('vnnic.sync.resubmit.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Detail

type Action = 'outcome' | 'resubmit' | null;

function TransactionDetail({ tx, row, onChanged }: { tx: VnnicSyncTransaction; row: VnnicSyncRow; onChanged: (message: string) => void }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  const [action, setAction] = useState<Action>(null);
  const outcomeAllowed = canRecordOutcome(tx);
  const resubmitAllowed = canResubmit(row);

  return (
    <div className="space-y-5">
      <KeyValue
        columns={3}
        rows={[
          [
            t('vnnic.sync.tx.transaction'),
            <EntityRef
              key="id"
              label={t('syncTx.heading', { attempt: tx.attempt })}
              to={`/compliance/vnnic/sync-transactions/${encodeURIComponent(tx.id)}`}
              id={tx.id}
            />,
          ],
          [t('vnnic.sync.tx.status'), <EnumBadge key="s" group="txStatus" value={tx.status} tones={TX_STATUS} />],
          [t('vnnic.sync.tx.operation'), label('syncOperation', tx.operation)],
          [t('vnnic.sync.tx.attempt'), tx.attempt],
          [t('vnnic.sync.tx.adapter'), <code key="a">{tx.adapter}</code>],
          [t('vnnic.sync.tx.schemaVersion'), <code key="v">{tx.schema_version}</code>],
          [t('vnnic.sync.tx.submitted'), formatDateTime(tx.submitted_at)],
          [t('vnnic.sync.tx.completed'), formatDateTime(tx.completed_at)],
          [t('vnnic.sync.tx.reference'), tx.external_reference ? <code key="r">{tx.external_reference}</code> : null],
          tx.error_code || tx.error_message
            ? [
                t('vnnic.sync.tx.error'),
                <span key="e">
                  {tx.error_code ? <SyncErrorCode code={tx.error_code} /> : null} {tx.error_message}
                </span>,
              ]
            : false,
        ]}
      />
      <div className="grid gap-2">
        <Payload label={t('vnnic.sync.tx.requestPayload')} value={tx.request_payload} />
        <Payload label={t('vnnic.sync.tx.responsePayload')} value={tx.response_payload} />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">{t('vnnic.sync.tx.receipts')}</h3>
        <ReceiptsTable receipts={tx.receipts} statusMap={TX_STATUS} />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">{t('vnnic.sync.tx.actions')}</h3>
        {outcomeAllowed || resubmitAllowed ? (
          <div className="flex flex-wrap gap-2">
            {outcomeAllowed ? (
              <Button onClick={() => setAction('outcome')}>
                <ClipboardCheck /> {t('vnnic.sync.outcome.submit')}
              </Button>
            ) : null}
            {resubmitAllowed ? (
              <Button variant="outline" onClick={() => setAction('resubmit')}>
                <Send /> {t('vnnic.sync.resubmit.open')}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('vnnic.sync.tx.noAction')}</p>
        )}
      </div>
      <ActionDialog
        open={action === 'outcome' && outcomeAllowed}
        onOpenChange={(o) => setAction(o ? 'outcome' : null)}
        title={t('vnnic.sync.outcome.title', { attempt: tx.attempt })}
        wide
      >
        <RecordOutcomeForm
          tx={tx}
          onCancel={() => setAction(null)}
          onDone={() => {
            setAction(null);
            onChanged(t('vnnic.sync.outcome.done'));
          }}
        />
      </ActionDialog>
      <ActionDialog
        open={action === 'resubmit' && resubmitAllowed}
        onOpenChange={(o) => setAction(o ? 'resubmit' : null)}
        title={t('vnnic.sync.resubmit.title', { domain: row.domain })}
      >
        <ResubmitForm
          dossierId={row.dossier_id}
          onCancel={() => setAction(null)}
          onDone={() => {
            setAction(null);
            onChanged(t('vnnic.sync.resubmit.done'));
          }}
        />
      </ActionDialog>
    </div>
  );
}

function SyncDetail({ row, version, onClose, onChanged }: { row: VnnicSyncRow; version: number; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  const txId = row.last_transaction_id;
  const tx = useApi((signal) => (txId ? api.getVnnicSyncTransaction(txId, { signal }) : Promise.resolve(null)), [txId, version]);
  const [notice, setNotice] = useState<string | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  // Mounted per dossier (keyed by the page), so this runs once per selection.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  const changed = (message: string) => {
    setNotice(message);
    onChanged();
  };

  return (
    <Section
      title={
        <h2 id="sync-detail-title" ref={heading} tabIndex={-1} className="outline-none">
          {row.domain}
        </h2>
      }
      actions={
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('vnnic.sync.closeDetails')}>
          <X />
        </Button>
      }
    >
      <div className="space-y-5">
        {notice ? <SuccessNote>{notice}</SuccessNote> : null}
        <KeyValue
          columns={3}
          rows={[
            [t('vnnic.sync.detail.syncStatus'), <EnumBadge key="s" group="syncStatus" value={row.status} tones={SYNC_STATUS} />],
            [t('vnnic.sync.detail.operation'), label('syncOperation', row.operation)],
            [
              t('vnnic.sync.detail.dossier'),
              <span key="d" className="flex flex-wrap items-center gap-1.5">
                {t('syncTx.dossierValue', { version: row.dossier_version })}
                <EnumBadge group="dossierStatus" value={row.dossier_status} tones={DOSSIER} />
              </span>,
            ],
            [
              t('vnnic.sync.detail.domain'),
              <EntityRef key="dm" label={row.domain} to={`/domains/${encodeURIComponent(row.domain)}`} id={row.domain_id} />,
            ],
            [t('vnnic.sync.detail.attempts'), row.attempts],
            [t('vnnic.sync.detail.reference'), row.external_reference ? <code key="r">{row.external_reference}</code> : null],
            [t('vnnic.sync.detail.lastError'), row.last_error_code ? <SyncErrorCode key="e" code={row.last_error_code} /> : null],
            [t('vnnic.sync.detail.nextAttempt'), row.next_attempt_at ? `${formatDateTime(row.next_attempt_at)} (${formatRelative(row.next_attempt_at)})` : null],
            [t('vnnic.sync.detail.statusChanged'), `${formatDateTime(row.status_changed_at)} (${formatRelative(row.status_changed_at)})`],
          ]}
        />

        <div className="space-y-3 border-t pt-4">
          <h3 className="text-sm font-semibold">{t('vnnic.sync.detail.lastTransaction')}</h3>
          {!txId ? <p className="text-sm text-muted-foreground">{t('vnnic.sync.detail.nothingSubmitted')}</p> : null}
          {txId && tx.status === 'loading' ? <Loading label={t('vnnic.sync.detail.loadingTx')} /> : null}
          {tx.status === 'error' ? <ErrorState error={tx.error} onRetry={tx.reload} title={t('vnnic.sync.detail.txError')} /> : null}
          {tx.status === 'success' && tx.data ? <TransactionDetail key={tx.data.id} tx={tx.data} row={row} onChanged={changed} /> : null}
          {txId === null && canResubmit(row) ? (
            <>
              <Button variant="outline" onClick={() => setResubmitting(true)}>
                <Send /> {t('vnnic.sync.resubmit.open')}
              </Button>
              <ActionDialog open={resubmitting} onOpenChange={setResubmitting} title={t('vnnic.sync.resubmit.title', { domain: row.domain })}>
                <ResubmitForm
                  dossierId={row.dossier_id}
                  onCancel={() => setResubmitting(false)}
                  onDone={() => {
                    setResubmitting(false);
                    changed(t('vnnic.sync.resubmit.done'));
                  }}
                />
              </ActionDialog>
            </>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Page

export function VnnicDossierSyncPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  useDocumentTitle(t('vnnic.sync.title'));
  const [params, setParams] = useSearchParams();
  const rawStatus = params.get('status');
  const status = isSyncStatus(rawStatus) ? rawStatus : undefined;
  const selectedId = params.get('dossier');
  const [version, setVersion] = useState(0);
  const result = useApi((signal) => api.listVnnicSync({ status, limit: LIST_LIMIT }, { signal }), [status, version]);

  const update = (changes: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v) p.set(k, v);
          else p.delete(k);
        }
        return p;
      },
      { replace: true },
    );

  const rows = result.status === 'success' ? result.data.items : [];
  // Keep the detail panel mounted (with its confirmation) while the list reloads after an action.
  const lastRows = useRetained(result.status === 'success' ? result.data.items : undefined);
  const selected = selectedId ? lastRows?.find((r) => r.dossier_id === selectedId) : undefined;
  const refresh = () => setVersion((n) => n + 1);

  const columns: Column<VnnicSyncRow>[] = [
    {
      key: 'domain',
      header: t('vnnic.sync.col.domain'),
      cell: (r) => (
        <EntityRef
          label={r.domain}
          to={`/domains/${encodeURIComponent(r.domain)}`}
          id={r.domain_id}
          sub={`${t('vnnic.sync.dossier')} ${t('syncTx.dossierValue', { version: r.dossier_version })}`}
        />
      ),
    },
    {
      key: 'op',
      header: t('vnnic.sync.col.operation'),
      cell: (r) => label('syncOperation', r.operation),
    },
    {
      key: 'status',
      header: t('vnnic.sync.col.status'),
      cell: (r) => <EnumBadge group="syncStatus" value={r.status} tones={SYNC_STATUS} />,
    },
    {
      key: 'attempts',
      header: t('vnnic.sync.col.attempts'),
      numeric: true,
      cell: (r) => r.attempts,
    },
    {
      key: 'ref',
      header: t('vnnic.sync.col.reference'),
      cell: (r) =>
        r.last_error_code ? (
          <SyncErrorCode code={r.last_error_code} />
        ) : r.external_reference ? (
          <code className="text-xs">{r.external_reference}</code>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'next',
      header: t('vnnic.sync.col.nextAttempt'),
      cell: (r) => <When at={r.next_attempt_at} />,
    },
    {
      key: 'changed',
      header: t('vnnic.sync.col.changed'),
      cell: (r) => <When at={r.status_changed_at} />,
    },
    {
      key: 'details',
      header: <span className="sr-only">{t('common.details')}</span>,
      cell: (r) => {
        const isSelected = r.dossier_id === selectedId;
        return (
          <Button
            size="sm"
            variant={isSelected ? 'secondary' : 'outline'}
            onClick={() => update({ dossier: isSelected ? null : r.dossier_id })}
            aria-expanded={isSelected}
            aria-controls={isSelected ? 'sync-detail' : undefined}
          >
            {isSelected ? t('common.hide') : t('common.details')}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={t('vnnic.sync.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('vnnic.sync.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={refresh} disabled={result.status === 'loading'}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />

      <div className="space-y-6">
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              <>
                {result.status === 'success' ? (
                  <span className="text-xs text-muted-foreground">
                    {t('vnnic.sync.count', { count: rows.length })}
                    {rows.length >= LIST_LIMIT ? ` · ${t('common.showingFirst', { count: LIST_LIMIT })}` : ''}
                  </span>
                ) : null}
                {status ? (
                  <Button variant="ghost" size="sm" onClick={() => update({ status: null })}>
                    <RotateCcw /> {tc('action.reset')}
                  </Button>
                ) : null}
              </>
            }
          >
            <SelectField
              label={t('common.status')}
              value={status ?? ''}
              allLabel={t('vnnic.sync.allStatuses')}
              options={options('syncStatus', SYNC_STATUS_ORDER)}
              onChange={(v) => update({ status: v || null })}
              className="w-full sm:w-56"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading label={t('vnnic.sync.loading')} rows={4} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.sync.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(r) => r.dossier_id}
              empty={
                <EmptyState
                  title={
                    status
                      ? t('vnnic.sync.emptyStatus', {
                          status: label('syncStatus', status),
                        })
                      : t('vnnic.sync.empty')
                  }
                />
              }
            />
          ) : null}
        </Section>

        {selectedId && lastRows ? (
          <div id="sync-detail">
            {selected ? (
              <SyncDetail key={selected.dossier_id} row={selected} version={version} onClose={() => update({ dossier: null })} onChanged={refresh} />
            ) : (
              <Section>
                <EmptyState title={t('vnnic.sync.notInList')}>
                  <p>{t('vnnic.sync.notInListHint')}</p>
                  {status ? (
                    <Button className="mt-3" size="sm" variant="outline" onClick={() => update({ status: null })}>
                      {t('vnnic.sync.showAllStatuses')}
                    </Button>
                  ) : null}
                </EmptyState>
              </Section>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
